import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  Radio,
  Button,
  Modal,
  Input,
  message,
  Tag,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { getViolations, revokeSuspension } from "../../Services/moderationApi";
import StatusPill from "../../Components/Moderation/StatusPill";

const FILTERS = [
  { key: "active", label: "Active" },
  { key: "revoked", label: "Revoked" },
  { key: "expired", label: "Expired" },
  { key: "all", label: "All" },
];

const DURATION_LABEL = {
  "3_days": "3 days",
  "7_days": "7 days",
  permanent: "Permanent",
};

const Violations = () => {
  const [filter, setFilter] = useState("active");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getViolations(
        filter === "all" ? {} : { status: filter }
      );
      setRows(resp?.data?.data || []);
    } catch (err) {
      message.error("Could not load suspensions");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const doRevoke = (violation) => {
    let reason = "";
    Modal.confirm({
      title: "Revoke this suspension?",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            The user regains access immediately and is notified. Their strike
            count is <b>not</b> reduced.
          </p>
          <Input.TextArea
            rows={3}
            placeholder="Reason for revoking (required)"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </div>
      ),
      okText: "Revoke",
      cancelText: "Cancel",
      onOk: async () => {
        if (!reason.trim()) {
          message.warning("A reason is required");
          throw new Error("reason required");
        }
        try {
          const resp = await revokeSuspension(
            violation.id || violation._key,
            reason.trim()
          );
          if (resp?.data?.error) {
            message.error(resp.data.error);
            throw new Error(resp.data.error);
          }
          message.success(resp?.data?.message || "Suspension revoked");
          load();
        } catch (err) {
          if (err?.response?.data?.error) {
            message.error(err.response.data.error);
          }
          throw err;
        }
      },
    });
  };

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      render: (u) => (u && u.name ? u.name : "(no name)"),
    },
    {
      title: "Type",
      dataIndex: "user_type",
      responsive: ["lg"],
    },
    {
      title: "Strike",
      dataIndex: "strike_number",
      render: (n) => <Tag>{n}</Tag>,
    },
    {
      title: "Duration",
      dataIndex: "suspension_type",
      render: (t) => DURATION_LABEL[t] || t,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      responsive: ["md"],
    },
    {
      title: "Ends",
      dataIndex: "suspension_end",
      render: (d) =>
        d ? moment(d).format("DD MMM YYYY HH:mm") : <Tag color="red">never</Tag>,
      responsive: ["md"],
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => <StatusPill status={s} />,
    },
    {
      title: "Appeal",
      dataIndex: "appeal_status",
      render: (s) => (s ? <StatusPill status={s} /> : "—"),
      responsive: ["lg"],
    },
    {
      title: "",
      key: "action",
      render: (_, row) =>
        row.status === "active" ? (
          <Button size="small" danger onClick={() => doRevoke(row)}>
            Revoke
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: "0 4px" }}>
      <Radio.Group
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ marginBottom: 16 }}
      >
        {FILTERS.map((f) => (
          <Radio.Button key={f.key} value={f.key}>
            {f.label}
          </Radio.Button>
        ))}
      </Radio.Group>

      <Table
        rowKey={(r) => r.id || r._key}
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ pageSize: 20 }}
      />
    </div>
  );
};

export default Violations;
